import { RICH_SECTION_BLOCK_NODE_TYPES } from "@repo/shared";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import type { JSONContent } from "@tiptap/react";
import { DATA_CATALOG_ENTRY_NODE_NAME } from "../data-catalog-entry-node";
import { DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME } from "../data-catalog-template-entry-node";
import type { FieldConfig } from "../nodes/types";
import type { DocumentSchemaConfig } from "./types";

type NodeAttrs = Record<string, unknown>;

type SanitizeJsonNodeResult = {
  node: JSONContent | null;
  changed: boolean;
};

const INLINE_NODE_TYPES = new Set([
  "text",
  "hardBreak",
  "mention",
  "documentEvidence",
  DATA_CATALOG_ENTRY_NODE_NAME,
]);
const PARAGRAPH_SECTION_CHILD_TYPES = new Set(["paragraph"]);
/**
 * The shared block grammar (which the Convex draft projection also enforces)
 * plus the web-app-only catalog template embed. Extending the shared list here
 * is fine; narrowing it is not — a node the backend may write but this validator
 * rejects makes the stored document unreadable and throws on page open (LAT-938).
 */
export const RICH_SECTION_CHILD_TYPES = new Set<string>([
  ...RICH_SECTION_BLOCK_NODE_TYPES,
  DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME,
]);

function arraysEqual(left: unknown, right: unknown): boolean {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
}

/**
 * Section guidance is optional, schema-owned display metadata. Empty or
 * whitespace-only guidance is treated as "no guidance" everywhere it is
 * compared, so the canonicalizer (which rewrites field attrs) and the
 * validator (which rejects stale field attrs) can never disagree about
 * whether a field has guidance. Mirrors the normalization applied when
 * schemas are compiled in `toFieldConfig` (schema-factory.ts).
 *
 * This matters because Tiptap materializes the `guidance` attr with its
 * empty-string default on edit; without a single shared notion of "absent
 * guidance" an edited `guidance: ""` is rejected as stale metadata (LAT-628).
 */
function normalizeGuidance(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function formatValidationError(schema: DocumentSchemaConfig, message: string): Error {
  const tabLabel =
    typeof schema.tabKey === "string" && schema.tabKey.length > 0
      ? `tab '${schema.tabKey}'`
      : `entity '${schema.entityType}'`;
  return new Error(`Stored document for ${tabLabel} is invalid: ${message}`);
}

function validateInlineContent(
  schema: DocumentSchemaConfig,
  context: string,
  content: JSONContent[] | undefined
): void {
  if (!Array.isArray(content)) {
    return;
  }

  for (const child of content) {
    if (!child || typeof child.type !== "string" || !INLINE_NODE_TYPES.has(child.type)) {
      throw formatValidationError(schema, `${context} contains unsupported inline content.`);
    }

    if (child.type === "text" && (typeof child.text !== "string" || child.text.length === 0)) {
      throw formatValidationError(schema, `${context} contains an empty text node.`);
    }
  }
}

function validateParagraphNode(
  schema: DocumentSchemaConfig,
  context: string,
  node: JSONContent
): void {
  if (node.type !== "paragraph") {
    throw formatValidationError(schema, `${context} must contain paragraph nodes only.`);
  }
  validateInlineContent(schema, `${context} paragraph`, node.content);
}

function validateHeadingNode(
  schema: DocumentSchemaConfig,
  context: string,
  node: JSONContent
): void {
  if (node.type !== "heading" || typeof node.attrs?.level !== "number") {
    throw formatValidationError(schema, `${context} heading nodes must declare a numeric level.`);
  }
  validateInlineContent(schema, `${context} heading`, node.content);
}

function validateListItemNode(
  schema: DocumentSchemaConfig,
  context: string,
  node: JSONContent
): void {
  if (node.type !== "listItem" || !Array.isArray(node.content) || node.content.length === 0) {
    throw formatValidationError(schema, `${context} list items must contain paragraph content.`);
  }

  let hasParagraph = false;
  for (const child of node.content) {
    if (child.type === "paragraph") {
      hasParagraph = true;
    }

    if (!child || typeof child.type !== "string" || !RICH_SECTION_CHILD_TYPES.has(child.type)) {
      throw formatValidationError(
        schema,
        `${context} list items may only contain supported rich-text blocks.`
      );
    }

    validateRichTextBlock(schema, `${context} list item`, child);
  }

  if (!hasParagraph) {
    throw formatValidationError(schema, `${context} list items must contain paragraph content.`);
  }
}

function validateListNode(schema: DocumentSchemaConfig, context: string, node: JSONContent): void {
  if (
    (node.type !== "bulletList" && node.type !== "orderedList") ||
    !Array.isArray(node.content) ||
    node.content.length === 0
  ) {
    throw formatValidationError(schema, `${context} lists must contain one or more list items.`);
  }

  for (const child of node.content) {
    validateListItemNode(schema, `${context} list`, child);
  }
}

function validateBlockquoteNode(
  schema: DocumentSchemaConfig,
  context: string,
  node: JSONContent
): void {
  if (node.type !== "blockquote" || !Array.isArray(node.content) || node.content.length === 0) {
    throw formatValidationError(
      schema,
      `${context} blockquotes must contain one or more supported rich-text blocks.`
    );
  }

  for (const child of node.content) {
    if (!child || typeof child.type !== "string" || !RICH_SECTION_CHILD_TYPES.has(child.type)) {
      throw formatValidationError(
        schema,
        `${context} blockquotes may only contain supported rich-text blocks.`
      );
    }

    validateRichTextBlock(schema, `${context} blockquote`, child);
  }
}

function validateCodeBlockNode(
  schema: DocumentSchemaConfig,
  context: string,
  node: JSONContent
): void {
  if (node.type !== "codeBlock") {
    throw formatValidationError(schema, `${context} must be a code block.`);
  }

  if (!Array.isArray(node.content)) {
    return;
  }

  for (const child of node.content) {
    if (child.type !== "text" || typeof child.text !== "string") {
      throw formatValidationError(schema, `${context} code blocks may only contain text nodes.`);
    }
  }
}

/**
 * Validate the block grammar shared by rich sections and their nested
 * list/blockquote containers. Tiptap's listItem (`paragraph block*`) and
 * blockquote (`block+`) schemas accept these block nodes at the cursor, so
 * persistence must apply the same explicit allowlist instead of a narrower
 * second grammar that can reject editor-produced content.
 */
function validateRichTextBlock(
  schema: DocumentSchemaConfig,
  context: string,
  node: JSONContent
): void {
  switch (node.type) {
    case "paragraph":
      validateParagraphNode(schema, context, node);
      break;
    case "heading":
      validateHeadingNode(schema, context, node);
      break;
    case "bulletList":
    case "orderedList":
      validateListNode(schema, context, node);
      break;
    case "blockquote":
      validateBlockquoteNode(schema, context, node);
      break;
    case "codeBlock":
      validateCodeBlockNode(schema, context, node);
      break;
    // Leaf blocks carry no content to validate.
    case "horizontalRule":
    case "image":
    case DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME:
      break;
    default:
      throw formatValidationError(
        schema,
        `${context} contains unsupported child node '${String(node.type)}'.`
      );
  }
}

function validateSectionContent(
  schema: DocumentSchemaConfig,
  field: FieldConfig,
  node: JSONContent
): void {
  if (!Array.isArray(node.content) || node.content.length === 0) {
    throw formatValidationError(
      schema,
      `Field '${field.fieldId}' must contain one or more '${field.nodeType}' child blocks.`
    );
  }

  const allowedChildTypes =
    field.nodeType === "paragraphSection"
      ? PARAGRAPH_SECTION_CHILD_TYPES
      : RICH_SECTION_CHILD_TYPES;

  for (const child of node.content) {
    if (!child || typeof child.type !== "string" || !allowedChildTypes.has(child.type)) {
      throw formatValidationError(
        schema,
        `Field '${field.fieldId}' contains unsupported child node '${String(child?.type)}'.`
      );
    }

    validateRichTextBlock(schema, `Field '${field.fieldId}'`, child);
  }
}

function assertFieldNodeAttrs(
  schema: DocumentSchemaConfig,
  field: FieldConfig,
  node: JSONContent
): void {
  const attrs = (node.attrs ?? {}) as NodeAttrs;

  if (attrs.fieldId !== field.fieldId) {
    throw formatValidationError(
      schema,
      `Field '${field.fieldId}' is stored with fieldId '${String(attrs.fieldId)}'.`
    );
  }

  if (attrs.label !== field.label) {
    throw formatValidationError(
      schema,
      `Field '${field.fieldId}' is stored with label '${String(attrs.label)}'.`
    );
  }

  const expectedGuidance = normalizeGuidance(field.guidance);
  const actualGuidance = normalizeGuidance(attrs.guidance);
  if (actualGuidance !== undefined && actualGuidance !== expectedGuidance) {
    throw formatValidationError(
      schema,
      `Field '${field.fieldId}' is stored with stale guidance metadata.`
    );
  }

  if ("required" in attrs || "assistantGuidance" in attrs || "expectedMentionTypes" in attrs) {
    throw formatValidationError(
      schema,
      `Field '${field.fieldId}' contains deprecated schema-owned attrs.`
    );
  }

  switch (field.nodeType) {
    case "atomicField": {
      if (attrs.fieldType !== field.fieldType) {
        throw formatValidationError(
          schema,
          `Field '${field.fieldId}' is stored with fieldType '${String(attrs.fieldType)}'.`
        );
      }

      const expectedOptions = field.options ?? [];
      if (!arraysEqual(attrs.options, expectedOptions)) {
        throw formatValidationError(
          schema,
          `Field '${field.fieldId}' is stored with stale enum options.`
        );
      }

      if (typeof attrs.value !== "string") {
        throw formatValidationError(
          schema,
          `Field '${field.fieldId}' atomic value must be a string.`
        );
      }
      break;
    }
  }
}

function assertFieldNodeMatchesSchema(
  schema: DocumentSchemaConfig,
  field: FieldConfig,
  node: JSONContent
): void {
  if (!node || node.type !== field.nodeType) {
    throw formatValidationError(
      schema,
      `Field '${field.fieldId}' must be stored as node type '${field.nodeType}'.`
    );
  }

  assertFieldNodeAttrs(schema, field, node);

  switch (field.nodeType) {
    case "richSection":
    case "paragraphSection":
      validateSectionContent(schema, field, node);
      break;
    case "sectionTitle":
      validateInlineContent(schema, `Field '${field.fieldId}'`, node.content);
      break;
  }
}

function sanitizeStoredNode(node: JSONContent, parentType?: string): SanitizeJsonNodeResult {
  if (node.type === "text") {
    if (typeof node.text !== "string" || node.text.length === 0) {
      return { node: null, changed: true };
    }
    return { node, changed: false };
  }

  if (
    parentType === "richSection" &&
    node.type === DATA_CATALOG_ENTRY_NODE_NAME &&
    node.attrs?.embedKind === "template"
  ) {
    return {
      node: {
        ...node,
        type: DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME,
      },
      changed: true,
    };
  }

  let changed = false;
  let nextNode = node;

  if (Array.isArray(node.content)) {
    const nextContent: JSONContent[] = [];

    for (const child of node.content) {
      const result = sanitizeStoredNode(child, node.type);
      if (result.changed) {
        changed = true;
      }
      if (result.node) {
        nextContent.push(result.node);
      }
    }

    if (changed) {
      if (nextContent.length > 0) {
        nextNode = { ...nextNode, content: nextContent };
      } else {
        const { content: _content, ...rest } = nextNode;
        nextNode = rest;
      }
    }
  }

  if (
    parentType === "richSection" &&
    nextNode.type === "paragraph" &&
    Array.isArray(nextNode.content)
  ) {
    const meaningfulInlineChildren = nextNode.content.filter((child) => {
      if (child.type !== "text") {
        return true;
      }
      return typeof child.text === "string" && child.text.trim().length > 0;
    });

    if (
      meaningfulInlineChildren.length === 1 &&
      meaningfulInlineChildren[0]?.type === DATA_CATALOG_ENTRY_NODE_NAME &&
      meaningfulInlineChildren[0].attrs?.embedKind === "template"
    ) {
      return {
        node: {
          ...meaningfulInlineChildren[0],
          type: DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME,
        },
        changed: true,
      };
    }
  }

  return { node: nextNode, changed };
}

function sanitizeStoredDocument(doc: JSONContent): JSONContent {
  const result = sanitizeStoredNode(doc);
  return result.node ?? doc;
}

export function assertDocumentMatchesSchema(
  schema: DocumentSchemaConfig,
  doc: JSONContent
): JSONContent {
  const sanitizedDoc = sanitizeStoredDocument(doc);

  if (sanitizedDoc.type !== "doc" || !Array.isArray(sanitizedDoc.content)) {
    throw formatValidationError(schema, "Document root must be a doc with top-level content.");
  }

  if (sanitizedDoc.content.length !== schema.fields.length) {
    throw formatValidationError(
      schema,
      `Expected ${schema.fields.length} top-level fields but found ${sanitizedDoc.content.length}.`
    );
  }

  schema.fields.forEach((field, index) => {
    assertFieldNodeMatchesSchema(schema, field, sanitizedDoc.content?.[index] as JSONContent);
  });

  return sanitizedDoc;
}

export function canonicalizeStoredDocumentFieldAttrs(
  schema: DocumentSchemaConfig,
  doc: JSONContent
): JSONContent {
  if (doc.type !== "doc" || !Array.isArray(doc.content)) {
    return doc;
  }

  if (doc.content.length !== schema.fields.length) {
    return doc;
  }

  let changed = false;
  const nextContent = doc.content.map((node, index) => {
    const field = schema.fields[index];
    if (!field) return node;

    const { attrs, changed: attrsChanged } = buildCanonicalFieldNodeAttrs(
      field,
      node.type,
      (node.attrs ?? {}) as NodeAttrs
    );

    if (!attrsChanged) return node;

    changed = true;
    return { ...node, attrs };
  });

  if (!changed) {
    return doc;
  }

  return { ...doc, content: nextContent };
}

export function buildCanonicalFieldNodeAttrs(
  field: FieldConfig,
  nodeType: string | undefined,
  sourceAttrs: NodeAttrs
): { attrs: NodeAttrs; changed: boolean } {
  const attrs: NodeAttrs = { ...sourceAttrs };
  let changed = false;

  if (attrs.fieldId !== field.fieldId) {
    attrs.fieldId = field.fieldId;
    changed = true;
  }

  if (attrs.label !== field.label) {
    attrs.label = field.label;
    changed = true;
  }

  const nextGuidance = normalizeGuidance(field.guidance);
  // Compare against the verbatim stored value (not normalized) so empty or
  // whitespace-only guidance is actively removed rather than left in place.
  const currentGuidance = typeof attrs.guidance === "string" ? attrs.guidance : undefined;
  if (currentGuidance !== nextGuidance) {
    if (nextGuidance !== undefined) {
      attrs.guidance = nextGuidance;
    } else if ("guidance" in attrs) {
      delete attrs.guidance;
    }
    changed = true;
  }

  if ("required" in attrs) {
    delete attrs.required;
    changed = true;
  }

  if ("assistantGuidance" in attrs) {
    delete attrs.assistantGuidance;
    changed = true;
  }

  if ("expectedMentionTypes" in attrs) {
    delete attrs.expectedMentionTypes;
    changed = true;
  }

  if (field.nodeType !== "atomicField") {
    const nextChromeless = field.chromeless === true;
    if (nextChromeless) {
      if (attrs.chromeless !== true) {
        attrs.chromeless = true;
        changed = true;
      }
    } else if ("chromeless" in attrs) {
      delete attrs.chromeless;
      changed = true;
    }
  }

  if (nodeType === "atomicField" && field.nodeType === "atomicField") {
    if (attrs.fieldType !== field.fieldType) {
      attrs.fieldType = field.fieldType;
      changed = true;
    }

    const nextOptions = field.options ?? [];
    if (!arraysEqual(attrs.options, nextOptions)) {
      attrs.options = nextOptions;
      changed = true;
    }
  }

  return { attrs, changed };
}

export function applyCanonicalTopLevelFieldAttrs(
  schema: DocumentSchemaConfig,
  doc: PMNode,
  tr: Transaction
): { tr: Transaction; hasChanges: boolean } {
  if (doc.childCount !== schema.fields.length) {
    throw formatValidationError(
      schema,
      `Expected ${schema.fields.length} top-level fields after edit but found ${doc.childCount}.`
    );
  }

  let nextTr = tr;
  let hasChanges = false;

  doc.forEach((node, offset, index) => {
    const field = schema.fields[index];
    if (!field) return;

    const { attrs, changed } = buildCanonicalFieldNodeAttrs(
      field,
      node.type.name,
      (node.attrs ?? {}) as NodeAttrs
    );
    if (!changed) return;

    hasChanges = true;
    nextTr = nextTr.setNodeMarkup(offset, undefined, attrs, node.marks);
  });

  return { tr: nextTr, hasChanges };
}
